
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Share, Users, Gift, Copy, Check } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

const ReferralSystem = () => {
  const [referralCode] = useState('GOLDEN-ABC123');
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();

  const referralStats = {
    totalReferrals: 12,
    activeReferrals: 8,
    totalEarnings: 240,
    pendingRewards: 45
  };

  const referralHistory = [
    {
      id: 1,
      friendName: 'Sarah M.',
      joinDate: '2025-01-20',
      status: 'active',
      reward: 25,
      friendSpent: 150
    },
    {
      id: 2,
      friendName: 'Mike R.',
      joinDate: '2025-01-18',
      status: 'active',
      reward: 20,
      friendSpent: 100
    },
    {
      id: 3,
      friendName: 'Emma T.',
      joinDate: '2025-01-15',
      status: 'inactive',
      reward: 15,
      friendSpent: 75
    }
  ];

  const rewardTiers = [
    { referrals: 1, bonus: '$5', status: 'completed' },
    { referrals: 5, bonus: '$30', status: 'completed' },
    { referrals: 10, bonus: '$75', status: 'completed' },
    { referrals: 25, bonus: '$200', status: 'locked' },
    { referrals: 50, bonus: '$500', status: 'locked' }
  ];

  const copyReferralCode = () => {
    navigator.clipboard.writeText(`https://goldenticketlucky.com/join?ref=${referralCode}`);
    setCopied(true);
    toast({
      title: "Referral link copied!",
      description: "Share it with friends to earn rewards together"
    });
    setTimeout(() => setCopied(false), 2000);
  };

  const shareToSocial = (platform: string) => {
    const text = `🎰 Join me on GoldenTicketLucky and get $5 bonus! Use my code: ${referralCode}`;
    const url = `https://goldenticketlucky.com/join?ref=${referralCode}`;
    
    let shareUrl = '';
    switch (platform) {
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
        break;
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}`;
        break;
    }
    
    window.open(shareUrl, '_blank');
  };

  return (
    <div className="space-y-6">
      {/* Referral Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <Users className="w-6 h-6 text-blue-600 mx-auto mb-2" />
            <p className="text-sm text-gray-600">Total Referrals</p>
            <p className="text-2xl font-bold text-blue-600">{referralStats.totalReferrals}</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4 text-center">
            <Users className="w-6 h-6 text-green-600 mx-auto mb-2" />
            <p className="text-sm text-gray-600">Active Friends</p>
            <p className="text-2xl font-bold text-green-600">{referralStats.activeReferrals}</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4 text-center">
            <Gift className="w-6 h-6 text-yellow-600 mx-auto mb-2" />
            <p className="text-sm text-gray-600">Total Earned</p>
            <p className="text-2xl font-bold text-yellow-600">${referralStats.totalEarnings}</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4 text-center">
            <Gift className="w-6 h-6 text-purple-600 mx-auto mb-2" />
            <p className="text-sm text-gray-600">Pending</p>
            <p className="text-2xl font-bold text-purple-600">${referralStats.pendingRewards}</p>
          </CardContent>
        </Card>
      </div>

      {/* Share Your Code */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Share className="w-5 h-5 text-yellow-600" />
            Share & Earn
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-yellow-50 p-4 rounded-lg">
            <h3 className="font-bold text-gray-900 mb-2">How it works:</h3>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>• Your friend gets $5 bonus when they join</li>
              <li>• You earn $5 when they make their first purchase</li>
              <li>• Earn 5% of their spending for the first month</li>
              <li>• Unlock bonus rewards as you refer more friends</li>
            </ul>
          </div>

          <div>
            <label className="block text-gray-700 font-medium mb-2">Your Referral Link</label>
            <div className="flex gap-2">
              <Input
                value={`https://goldenticketlucky.com/join?ref=${referralCode}`}
                readOnly
                className="flex-1"
              />
              <Button
                onClick={copyReferralCode}
                variant="outline"
                className="flex items-center gap-2"
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copied ? 'Copied!' : 'Copy'}
              </Button>
            </div>
          </div>

          <div>
            <p className="text-gray-700 font-medium mb-3">Share on social media:</p>
            <div className="flex gap-2">
              <Button
                onClick={() => shareToSocial('twitter')}
                className="bg-blue-500 hover:bg-blue-600"
              >
                Twitter
              </Button>
              <Button
                onClick={() => shareToSocial('facebook')}
                className="bg-blue-700 hover:bg-blue-800"
              >
                Facebook
              </Button>
              <Button
                onClick={() => shareToSocial('whatsapp')}
                className="bg-green-600 hover:bg-green-700"
              >
                WhatsApp
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Reward Tiers */}
      <Card>
        <CardHeader>
          <CardTitle>Bonus Rewards</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {rewardTiers.map((tier, index) => (
              <div
                key={index}
                className={`flex items-center justify-between p-3 rounded-lg border ${
                  tier.status === 'completed' ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    tier.status === 'completed' ? 'bg-green-600 text-white' : 'bg-gray-400 text-white'
                  }`}>
                    {tier.status === 'completed' ? <Check className="w-4 h-4" /> : index + 1}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{tier.referrals} Referrals</p>
                    <p className="text-sm text-gray-600">Earn {tier.bonus} bonus</p>
                  </div>
                </div>
                <Badge variant={tier.status === 'completed' ? 'default' : 'secondary'} 
                       className={tier.status === 'completed' ? 'bg-green-100 text-green-800' : ''}>
                  {tier.status === 'completed' ? 'Unlocked' : 'Locked'}
                </Badge>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Referral History */}
      <Card>
        <CardHeader>
          <CardTitle>Your Referrals</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {referralHistory.map((referral) => (
              <div key={referral.id} className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <h4 className="font-medium text-gray-900">{referral.friendName}</h4>
                  <p className="text-sm text-gray-600">Joined {referral.joinDate}</p>
                  <p className="text-sm text-gray-600">Total spent: ${referral.friendSpent}</p>
                </div>
                <div className="text-right">
                  <Badge variant={referral.status === 'active' ? 'default' : 'secondary'} 
                         className={referral.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}>
                    {referral.status}
                  </Badge>
                  <p className="text-lg font-bold text-green-600 mt-1">+${referral.reward}</p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ReferralSystem;
